/**
 * @file src/pages/invoice-creation-wizard/components/TaxSummarySection.jsx
 *
 * What changed from the original:
 *  - Tax Rate Breakdown section now uses vatBreakdown prop instead of
 *    hardcoded "Standard Rate (15%) = totalTax, Zero Rate = 0, Exempt = 0"
 *  - vatBreakdown is optional — falls back gracefully if not provided
 *
 * Required index.jsx change:
 *  Add vatBreakdown prop to TaxSummarySection:
 *  <TaxSummarySection
 *    totals={formData.totals}
 *    currency={formData.invoice.currency}
 *    vatBreakdown={formData.vatBreakdown}  ← add this
 *  />
 */

import React, { useState } from "react";
import { motion } from "framer-motion";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";

// ---------------------------------------------------------------------------
// TaxSummarySection
// ---------------------------------------------------------------------------

const TaxSummarySection = ({ totals, currency, vatBreakdown = [] }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-hero-cta border border-gray-100 dark:border-gray-700"
    >
      <div className="p-6">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
              <Icon name="DollarSign" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground dark:text-white">
                Tax Summary
              </h3>
              <p className="text-sm text-muted-foreground dark:text-gray-300">
                Breakdown of taxes and totals
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            onClick={() => setIsExpanded(!isExpanded)}
          />
        </div>

        {/* Quick Totals */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="text-sm text-muted-foreground dark:text-gray-300 mb-1">
              Subtotal
            </div>
            <div className="text-lg font-semibold text-foreground dark:text-white">
              {currency} {totals.subtotal.toFixed(2)}
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div className="text-sm text-muted-foreground dark:text-gray-300 mb-1">
              Total Tax
            </div>
            <div className="text-lg font-semibold text-foreground dark:text-white">
              {currency} {totals.totalTax.toFixed(2)}
            </div>
          </div>
          <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4">
            <div className="text-sm text-primary mb-1">Total Amount</div>
            <div className="text-2xl font-bold text-primary">
              {currency} {totals.total.toFixed(2)}
            </div>
          </div>
        </div>

        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            {/* Detailed Breakdown */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <h4 className="font-medium text-foreground dark:text-white mb-4">
                Detailed Tax Breakdown
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground dark:text-gray-300">
                    Items Subtotal
                  </span>
                  <span className="font-medium text-foreground dark:text-white">
                    {currency} {totals.subtotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground dark:text-gray-300">
                    Total Discounts
                  </span>
                  <span className="font-medium text-error">
                    − {currency} {totals.totalDiscount.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground dark:text-gray-300">
                    Taxable Amount
                  </span>
                  <span className="font-medium text-foreground dark:text-white">
                    {currency} {totals.taxableAmount.toFixed(2)}
                  </span>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-600 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground dark:text-gray-300">
                      VAT
                    </span>
                    <span className="font-medium text-foreground dark:text-white">
                      {currency} {totals.totalTax.toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-600 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-foreground dark:text-white">
                      Total Amount
                    </span>
                    <span className="text-lg font-bold text-primary">
                      {currency} {totals.total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tax Rate Breakdown — dynamic from vatBreakdown */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-4">
                Tax Rate Breakdown
              </h4>
              <div className="space-y-2">
                {vatBreakdown.length > 0 ? (
                  vatBreakdown.map((vat, index) => {
                    const label =
                      vat.vatCategoryCode === "S"
                        ? `Standard Rate (${vat.vatRate}%)`
                        : vat.vatCategoryCode === "Z"
                          ? `Zero Rate (0%)`
                          : vat.vatCategoryCode === "E"
                            ? `Exempt`
                            : vat.vatCategoryCode === "O"
                              ? `Out of Scope`
                              : `VAT ${vat.vatRate}%`;

                    return (
                      <div
                        key={index}
                        className="flex justify-between items-center"
                      >
                        <span className="text-blue-700 dark:text-blue-300">
                          {label}
                        </span>
                        <span className="font-medium text-blue-900 dark:text-blue-100">
                          {currency} {(vat.taxAmount ?? 0).toFixed(2)}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  // Fallback when vatBreakdown not yet calculated
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-blue-700 dark:text-blue-300">
                        Standard Rate (15%)
                      </span>
                      <span className="font-medium text-blue-900 dark:text-blue-100">
                        {currency} {totals.totalTax.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-blue-700 dark:text-blue-300">
                        Zero Rate (0%)
                      </span>
                      <span className="font-medium text-blue-900 dark:text-blue-100">
                        {currency} 0.00
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-blue-700 dark:text-blue-300">
                        Exempt
                      </span>
                      <span className="font-medium text-blue-900 dark:text-blue-100">
                        {currency} 0.00
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ZATCA Compliance Notice */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon
                  name="Shield"
                  size={20}
                  className="text-amber-600 dark:text-amber-400 mt-0.5"
                />
                <div>
                  <h5 className="font-medium text-amber-900 dark:text-amber-100 mb-1">
                    ZATCA Tax Compliance
                  </h5>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    Tax calculations are automatically validated against ZATCA
                    Phase 1 and Phase 2 requirements. All rates and exemptions
                    comply with Saudi Arabian tax regulations.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default TaxSummarySection;
